import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { TourLauncherButton } from "@/contexts/TourContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DollarSign, Plus, X, Play, Users, Sparkles, Calendar, AlertTriangle, Save, TestTube } from "lucide-react";
import { toast } from "sonner";

/**
 * R151 — Per-property dues schedule editor. Owns three subtables:
 *   • Tier list (unit_type → amount) — a `default` tier is required
 *   • Unit overrides (unit_number → amount) for penthouses / oddballs
 *   • Late fee config (grace, first fee, escalating recurring)
 * Plus "Preview next run" and "Generate now" for admins who want to see
 * exactly what the daily cron will bill before it fires.
 */
export default function DuesSchedulePage() {
  const { properties, propertyId } = useProperty();
  const [scope, setScope] = useState(propertyId || properties[0]?.id || "");
  const [schedule, setSchedule] = useState(null);
  const [units, setUnits] = useState([]);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    if (properties.length && !scope) setScope(properties[0].id);
  }, [properties, scope]);

  const load = useCallback(async () => {
    if (!scope) return;
    try {
      const [{ data: s }, { data: u }] = await Promise.all([
        api.get("/admin/dues/schedule", { params: { property_id: scope } }),
        api.get("/admin/dues/units", { params: { property_id: scope } }),
      ]);
      setSchedule(s);
      setUnits(u.items || []);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load"); }
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  const patchTier = (idx, patch) => {
    setSchedule((s) => ({
      ...s,
      tiers: s.tiers.map((t, i) => (i === idx ? { ...t, ...patch } : t)),
    }));
  };

  const addTier = () => {
    setSchedule((s) => ({ ...s, tiers: [...s.tiers, { unit_type: "", amount: 0 }] }));
  };

  const removeTier = (idx) => {
    setSchedule((s) => {
      const t = s.tiers[idx];
      if (t?.unit_type === "default") {
        toast.error("The `default` tier is required — every property needs a fallback amount.");
        return s;
      }
      return { ...s, tiers: s.tiers.filter((_, i) => i !== idx) };
    });
  };

  const patchOverride = (idx, patch) => {
    setSchedule((s) => ({
      ...s,
      unit_overrides: s.unit_overrides.map((o, i) => (i === idx ? { ...o, ...patch } : o)),
    }));
  };

  const addOverride = () => {
    setSchedule((s) => ({
      ...s,
      unit_overrides: [...(s.unit_overrides || []), { unit_number: "", amount: 0 }],
    }));
  };

  const removeOverride = (idx) => {
    setSchedule((s) => ({
      ...s,
      unit_overrides: s.unit_overrides.filter((_, i) => i !== idx),
    }));
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/dues/schedule", schedule);
      toast.success("Dues schedule saved");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Save failed"); }
    finally { setBusy(false); }
  };

  const runBackfill = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/dues/units-backfill?property_id=${scope}`);
      toast.success(`Backfilled ${data.created} unit${data.created === 1 ? "" : "s"} from residents (${data.total_residents} residents scanned)`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Backfill failed"); }
    finally { setBusy(false); }
  };

  const runPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/dues/preview", { params: { property_id: scope } });
      setPreview(data);
      setPreviewOpen(true);
    } catch (e) { toast.error(apiErrorMessage(e) || "Preview failed"); }
    finally { setBusy(false); }
  };

  const runGenerate = async () => {
    if (!window.confirm(`Generate this month's charges for ${scopeName} right now? Idempotent — units already billed this period are skipped.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/dues/generate", { property_id: scope });
      toast.success(`Generated ${data.charges_created} charge${data.charges_created === 1 ? "" : "s"} · skipped ${data.skipped_already_billed} · total $${data.total_billed.toFixed(2)}`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Generate failed"); }
    finally { setBusy(false); }
  };

  const patchUnit = async (u, patch) => {
    try {
      await api.patch(`/admin/dues/units/${u.id}`, patch);
      setUnits((cur) => cur.map((x) => (x.id === u.id ? { ...x, ...patch } : x)));
    } catch (e) { toast.error(apiErrorMessage(e) || "Update failed"); }
  };

  const runTestCheckout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/dues/test-checkout",
        { origin_url: window.location.origin });
      toast.success("Test checkout opened — complete or cancel to verify Stripe.");
      window.open(data.checkout_url, "_blank", "noopener,noreferrer");
    } catch (e) { toast.error(apiErrorMessage(e) || "Test checkout failed"); }
    finally { setBusy(false); }
  };

  const scopeName = useMemo(
    () => properties.find((p) => p.id === scope)?.name || scope,
    [properties, scope],
  );

  const unitTypeCounts = useMemo(() => {
    const map = {};
    for (const u of units) {
      const t = (u.unit_type || "default").trim() || "default";
      map[t] = (map[t] || 0) + 1;
    }
    return map;
  }, [units]);

  if (!schedule) return <div className="p-4 text-white/60">Loading…</div>;

  return (
    <div className="space-y-4">
      <PageHeader eyebrow="Finance" title="Dues Schedule"
                   subtitle="Per-property monthly dues rate table, per-unit overrides, and late-fee automation. The daily cron reads this to bill every unit."
                   actions={<TourLauncherButton name="dues_tour" />} />

      <div className="flex items-center gap-2 flex-wrap" data-tour="dues-scope">
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger className="w-64 h-9 bg-white/[0.04] border-white/10"
                          data-testid="dues-scope-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
            {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2 rounded-full border border-white/10 px-3 py-1">
          <Label htmlFor="enabled-toggle" className="text-xs cursor-pointer">
            {schedule.enabled ? "Enabled" : "Disabled"}
          </Label>
          <Switch id="enabled-toggle"
                  checked={!!schedule.enabled}
                  onCheckedChange={(v) => setSchedule({ ...schedule, enabled: v })}
                  data-testid="dues-enabled-toggle" />
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={runTestCheckout} disabled={busy}
                   className="border-emerald-500/40 text-emerald-300 rounded-full h-9"
                   data-testid="dues-test-checkout-btn" data-tour="dues-test-checkout">
            <TestTube className="h-4 w-4 mr-1" /> Test Stripe ($1)
          </Button>
          <Button variant="outline" onClick={runBackfill} disabled={busy}
                   className="border-white/10 rounded-full h-9"
                   data-testid="dues-backfill-btn">
            <Users className="h-4 w-4 mr-1" /> Backfill units
          </Button>
          <Button variant="outline" onClick={runPreview} disabled={busy}
                   className="border-orange-500/40 text-orange-300 rounded-full h-9"
                   data-testid="dues-preview-btn" data-tour="dues-preview">
            <Sparkles className="h-4 w-4 mr-1" /> Preview
          </Button>
          <Button onClick={runGenerate} disabled={busy || !schedule.enabled}
                   className="bg-orange-500 hover:bg-orange-600 rounded-full h-9"
                   data-testid="dues-generate-btn">
            <Play className="h-4 w-4 mr-1" /> Generate now
          </Button>
        </div>
      </div>

      {/* Schedule basics */}
      <Card className="p-4 border-white/10">
        <div className="flex items-center gap-2 mb-3">
          <Calendar className="h-4 w-4 text-orange-400" />
          <div className="overline">Schedule</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs">Due day of month <span className="text-white/40">(1–28)</span></Label>
            <Input type="number" min={1} max={28}
                   value={schedule.due_day}
                   onChange={(e) => setSchedule({ ...schedule, due_day: Number(e.target.value) || 1 })}
                   className="bg-white/[0.04] border-white/10"
                   data-testid="dues-due-day" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Memo on each charge</Label>
            <Input value={schedule.memo_template}
                   onChange={(e) => setSchedule({ ...schedule, memo_template: e.target.value })}
                   placeholder="Monthly HOA Dues · {month_name}"
                   className="bg-white/[0.04] border-white/10"
                   data-testid="dues-memo-template" />
            <div className="text-[10px] text-white/40 mt-1">
              <code>{"{month_name}"}</code> is replaced with e.g. "March 2026"
            </div>
          </div>
        </div>
      </Card>

      {/* Tier table */}
      <Card className="p-4 border-white/10" data-tour="dues-tiers">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-orange-400" />
            <div className="overline">Tier by unit type</div>
            <Badge variant="outline" className="text-[10px] border-white/10">
              {schedule.tiers.length} tier{schedule.tiers.length === 1 ? "" : "s"}
            </Badge>
          </div>
          <Button variant="outline" size="sm" onClick={addTier}
                  className="border-white/10 rounded-full h-8"
                  data-testid="dues-add-tier">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add tier
          </Button>
        </div>
        <div className="space-y-2">
          {schedule.tiers.map((t, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`dues-tier-${i}`}>
              <Input value={t.unit_type}
                     onChange={(e) => patchTier(i, { unit_type: e.target.value })}
                     placeholder="1BR, 2BR, Penthouse, default…"
                     disabled={t.unit_type === "default"}
                     className="bg-white/[0.04] border-white/10 flex-1 max-w-xs"
                     data-testid={`dues-tier-type-${i}`} />
              <span className="text-white/40">→</span>
              <div className="relative">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-white/40 text-xs">$</span>
                <Input type="number" min={0} step={0.01} value={t.amount}
                       onChange={(e) => patchTier(i, { amount: Number(e.target.value) })}
                       className="bg-white/[0.04] border-white/10 pl-6 w-32 tabular-nums"
                       data-testid={`dues-tier-amount-${i}`} />
              </div>
              <span className="text-[11px] text-white/40 min-w-[80px]">
                {unitTypeCounts[t.unit_type] || 0} unit{(unitTypeCounts[t.unit_type] || 0) === 1 ? "" : "s"}
              </span>
              {t.unit_type !== "default" && (
                <button onClick={() => removeTier(i)}
                        className="text-white/40 hover:text-red-400 p-1"
                        data-testid={`dues-tier-remove-${i}`}>
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
          <div className="text-[11px] text-white/40 pt-2 border-t border-white/5">
            💡 Assign a unit_type to each unit in the <strong>Units</strong> table below.
            Units with an unknown type fall back to the <code>default</code> tier.
          </div>
        </div>
      </Card>

      {/* Unit overrides */}
      <Card className="p-4 border-white/10">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="overline">Per-unit overrides</div>
            <Badge variant="outline" className="text-[10px] border-white/10">
              {schedule.unit_overrides?.length || 0}
            </Badge>
          </div>
          <Button variant="outline" size="sm" onClick={addOverride}
                  className="border-white/10 rounded-full h-8"
                  data-testid="dues-add-override">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add override
          </Button>
        </div>
        {(schedule.unit_overrides || []).length === 0 ? (
          <div className="text-xs text-white/40">
            Use overrides for oddballs like penthouses or ground-floor commercial —
            they bypass the tier table.
          </div>
        ) : (
          <div className="space-y-2">
            {schedule.unit_overrides.map((o, i) => (
              <div key={i} className="flex items-center gap-2"
                   data-testid={`dues-override-${i}`}>
                <Input value={o.unit_number}
                       onChange={(e) => patchOverride(i, { unit_number: e.target.value })}
                       placeholder="Unit number"
                       className="bg-white/[0.04] border-white/10 w-32"
                       data-testid={`dues-override-unit-${i}`} />
                <span className="text-white/40">→</span>
                <div className="relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-white/40 text-xs">$</span>
                  <Input type="number" min={0} step={0.01} value={o.amount}
                         onChange={(e) => patchOverride(i, { amount: Number(e.target.value) })}
                         className="bg-white/[0.04] border-white/10 pl-6 w-32 tabular-nums"
                         data-testid={`dues-override-amount-${i}`} />
                </div>
                <button onClick={() => removeOverride(i)}
                        className="text-white/40 hover:text-red-400 p-1"
                        data-testid={`dues-override-remove-${i}`}>
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Late fees */}
      <Card className="p-4 border-white/10">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="h-4 w-4 text-amber-400" />
          <div className="overline">Late fee automation</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Grace days</Label>
            <Input type="number" min={0} max={90} value={schedule.grace_days}
                   onChange={(e) => setSchedule({ ...schedule, grace_days: Number(e.target.value) })}
                   className="bg-white/[0.04] border-white/10"
                   data-testid="dues-grace-days" />
            <div className="text-[10px] text-white/40 mt-1">Days after due before any fee</div>
          </div>
          <div>
            <Label className="text-xs">First late fee ($)</Label>
            <Input type="number" min={0} step={0.01} value={schedule.first_late_fee_amount}
                   onChange={(e) => setSchedule({ ...schedule, first_late_fee_amount: Number(e.target.value) })}
                   className="bg-white/[0.04] border-white/10 tabular-nums"
                   data-testid="dues-first-late-fee" />
            <div className="text-[10px] text-white/40 mt-1">Applied once after grace</div>
          </div>
          <div>
            <Label className="text-xs">Recurring late fee ($)</Label>
            <Input type="number" min={0} step={0.01} value={schedule.recurring_late_fee_amount}
                   onChange={(e) => setSchedule({ ...schedule, recurring_late_fee_amount: Number(e.target.value) })}
                   className="bg-white/[0.04] border-white/10 tabular-nums"
                   data-testid="dues-recurring-late-fee" />
            <div className="text-[10px] text-white/40 mt-1">0 disables recurring</div>
          </div>
          <div>
            <Label className="text-xs">Every N days</Label>
            <Input type="number" min={0} max={365} value={schedule.recurring_late_fee_every_days}
                   onChange={(e) => setSchedule({ ...schedule, recurring_late_fee_every_days: Number(e.target.value) })}
                   className="bg-white/[0.04] border-white/10"
                   data-testid="dues-recurring-every-days" />
            <div className="text-[10px] text-white/40 mt-1">Cadence of recurring fees</div>
          </div>
        </div>
      </Card>

      <div className="sticky bottom-4 flex justify-end">
        <Button onClick={save} disabled={busy}
                className="bg-orange-500 hover:bg-orange-600 rounded-full h-10 px-6 shadow-lg shadow-orange-500/20"
                data-testid="dues-save-btn">
          <Save className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Save schedule"}
        </Button>
      </div>

      {/* Units editor */}
      <Card className="p-4 border-white/10">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="overline">Units — {scopeName}</div>
            <Badge variant="outline" className="text-[10px] border-white/10">
              {units.length} unit{units.length === 1 ? "" : "s"}
            </Badge>
          </div>
        </div>
        {units.length === 0 ? (
          <div className="text-xs text-white/40 text-center py-6">
            No units yet. Click <strong>Backfill units</strong> to auto-create
            them from residents that already have a <code>unit</code> string.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.03] text-xs">
                <tr>
                  <th className="text-left px-2 py-2 text-white/60">Unit</th>
                  <th className="text-left px-2 py-2 text-white/60">Type</th>
                  <th className="text-left px-2 py-2 text-white/60">Sqft</th>
                  <th className="text-left px-2 py-2 text-white/60">Owner</th>
                </tr>
              </thead>
              <tbody>
                {units.map((u) => (
                  <tr key={u.id} className="border-t border-white/5"
                      data-testid={`dues-unit-row-${u.id}`}>
                    <td className="px-2 py-2 font-mono text-xs">{u.unit_number}</td>
                    <td className="px-2 py-2">
                      <Select value={u.unit_type || "default"}
                              onValueChange={(v) => patchUnit(u, { unit_type: v })}>
                        <SelectTrigger className="h-8 w-40 bg-white/[0.04] border-white/10 text-xs"
                                        data-testid={`dues-unit-type-${u.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                          {schedule.tiers.map((t) => (
                            <SelectItem key={t.unit_type} value={t.unit_type}>
                              {t.unit_type} <span className="text-white/40">· ${t.amount}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-2 py-2">
                      <Input type="number" min={0} value={u.sqft || ""}
                             onChange={(e) => patchUnit(u, { sqft: Number(e.target.value) || null })}
                             placeholder="—"
                             className="h-8 w-20 bg-white/[0.04] border-white/10 text-xs"
                             data-testid={`dues-unit-sqft-${u.id}`} />
                    </td>
                    <td className="px-2 py-2 text-[11px] text-white/50">
                      {u.owner_id ? <code>{u.owner_id.slice(0, 8)}…</code> : <span className="text-white/30">unassigned</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Preview dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="bg-[#0a0a0b] border-white/10 text-white sm:max-w-[720px]"
                       data-testid="dues-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-orange-400" />
              Preview — {preview?.month_name}
            </DialogTitle>
          </DialogHeader>
          {preview && (
            <>
              <div className="grid grid-cols-3 gap-3 text-xs mb-3">
                <div className="rounded-md border border-white/10 p-3">
                  <div className="text-white/40 uppercase tracking-widest text-[10px]">Would bill</div>
                  <div className="text-2xl font-semibold text-emerald-300">{preview.totals.would_bill}</div>
                </div>
                <div className="rounded-md border border-white/10 p-3">
                  <div className="text-white/40 uppercase tracking-widest text-[10px]">Would skip</div>
                  <div className="text-2xl font-semibold text-amber-300">{preview.totals.would_skip}</div>
                </div>
                <div className="rounded-md border border-white/10 p-3">
                  <div className="text-white/40 uppercase tracking-widest text-[10px]">Total billed</div>
                  <div className="text-2xl font-semibold tabular-nums">${preview.totals.total_billed.toFixed(2)}</div>
                </div>
              </div>
              <div className="max-h-96 overflow-y-auto border border-white/10 rounded-md">
                <table className="w-full text-xs">
                  <thead className="bg-white/[0.03] sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1.5 text-white/60">Unit</th>
                      <th className="text-left px-2 py-1.5 text-white/60">Type</th>
                      <th className="text-right px-2 py-1.5 text-white/60">Amount</th>
                      <th className="text-left px-2 py-1.5 text-white/60">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((r) => (
                      <tr key={r.unit_id} className="border-t border-white/5">
                        <td className="px-2 py-1.5 font-mono">{r.unit_number}</td>
                        <td className="px-2 py-1.5">{r.unit_type}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">${r.amount.toFixed(2)}</td>
                        <td className="px-2 py-1.5">
                          {r.would_skip
                            ? <span className="text-amber-300/70">skip · {r.skip_reason}</span>
                            : <span className="text-emerald-300/80">will bill</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}
                    className="border-white/10">Close</Button>
            <Button onClick={() => { setPreviewOpen(false); runGenerate(); }}
                    className="bg-orange-500 hover:bg-orange-600"
                    data-testid="dues-preview-generate">
              <Play className="h-4 w-4 mr-1" /> Generate now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
